/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  browserLocalPersistence,
  getRedirectResult,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type AuthProvider,
} from 'firebase/auth'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AuthErrorMessage } from '@aglyn/shared-data-enums'
import { useAuth, useUser } from '@aglyn/tenant-feature-instance'
import {
  markInteractiveSignIn,
  markInteractiveSignOut,
} from '../utils/interactive-signin'
import isMobileBrowser from '../utils/is-mobile-browser'
import { createAuthProvider } from '../utils/oauth-providers'
import {
  describePasskeySignInFailure,
  signInWithPasskey,
  usePasskeysSupported,
} from '../utils/passkeys'
import {
  reauthRefusal,
  reauthRefusalText,
  standDownRefusedReauth,
} from '../utils/reauth-identity-guard'
import { clearSignInBounces } from '../utils/signin-bounce'
import {
  clearSessionReauth,
  dismissSessionReauth,
  getSessionReauth,
  markSessionReauthRedirect,
  reopenSessionReauth,
  subscribeSessionReauth,
  type SessionReauthState,
} from '../utils/session-reauth'

/**
 * "Sign in again to verify your device" (AGL-664).
 *
 * The in-place half of session loss: instead of the hard bounce to
 * `/signin`, this dialog opens over the current route, re-authenticates
 * with the account's own factors, and on success simply closes — no
 * navigation, no lost context. The authenticated layout holds its redirect
 * while a request is pending, so the page underneath stays mounted (in the
 * degraded, cache-backed state AGL-1062 documented) the whole time.
 *
 * ## Session semantics this must not soften
 *
 * A revoked or freshly-tombstoned session (AGL-462) is DELIBERATELY dead:
 * the trigger site has already signed the local user out by the time this
 * dialog appears, and the only way forward from here is a real credential
 * sign-in — password or a provider ceremony. Nothing in this component
 * reuses a cached token, the shared cookie, or any other silent path to
 * bring a killed session back. "Expired but locally alive" never reaches
 * this dialog at all: `useSessionCookie` re-mints those silently (AGL-624).
 *
 * The `stale` trigger (AGL-1063 heuristic) is the one case where the local
 * user is still signed in when the dialog opens. Its heal is the full
 * sign-out/sign-in cycle (`feedback_oneshot_getdocs_denied_listeners_ok`),
 * performed only when the user submits — until then nothing is touched, so
 * "Not now" costs nothing.
 *
 * Passkeys (AGL-662) are the additional factor here: a REAL WebAuthn
 * ceremony verified server-side and bridged through `signInWithCustomToken`
 * — a fresh credential sign-in, not a resurrected session, so it honors
 * the semantics above exactly like the password and provider paths.
 */

/**
 * Same selection logic as `reauthProvider` on the close-account card, but
 * from a captured provider-id string — by the time this dialog is up the
 * user object that carried `providerData` may already be signed out.
 */
export function providerForId(providerId: string | null): AuthProvider {
  // Delegated to the shared factory (AGL-1415), which also forces Google's
  // account chooser — re-authenticating against silently-whichever-account
  // the device holds is how a person verifies the wrong identity.
  return createAuthProvider(providerId)
}

function reasonText(state: SessionReauthState): string {
  switch (state.reason) {
    case 'revoked':
      return (
        'Your session was ended for security reasons. Sign in again to ' +
        'verify this device — you will stay right here, and nothing on ' +
        'this page is lost.'
      )
    case 'signed-out':
      return (
        'You signed out on another device or tab, which ended this ' +
        'session too. Sign in again to keep working — you will stay ' +
        'right here.'
      )
    case 'idle':
      return (
        'You were signed out after a period of inactivity. Sign in again ' +
        'to pick up exactly where you left off.'
      )
    // The loop breaker (AGL-2486). Says what the console actually observed
    // — it kept being sent back to sign in — and names the one cause a user
    // can act on, because another tab holding an older session is what
    // produced this on production and refreshing that tab is what fixed it.
    case 'unstable':
      return (
        'The console kept being sent back to sign in, so it stopped trying ' +
        'rather than keep redirecting you. This usually means another tab ' +
        'is holding an older session for this browser. Sign in again here ' +
        'to fix it — and if it returns, reload the other tab.'
      )
    default:
      return (
        'Your session can no longer read your data from the server. ' +
        'Signing in again fixes it without leaving this page — unsaved ' +
        'changes stay on screen.'
      )
  }
}

function signInErrorText(code: string | undefined): string {
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    return 'That password is not right.'
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many attempts — wait a moment and try again.'
  }
  if (code === 'auth/popup-closed-by-user') {
    return 'Sign-in canceled.'
  }
  return `Sign-in failed: ${code ?? 'unknown error'}`
}

export function SessionReauthDialog() {
  const auth = useAuth()
  const { data: user } = useUser()
  const [state, setState] = useState<SessionReauthState>(getSessionReauth)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Offered whenever the browser can run the ceremony: a discoverable
  // credential needs no captured identity, and if the user has none the
  // browser prompt simply comes up empty and cancels — no harm done.
  const passkeySupported = usePasskeysSupported()

  useEffect(() => subscribeSessionReauth(setState), [])

  const active = state.reason !== null

  // The session came back — our own sign-in below, or a sibling tab's
  // restore delivering a fresh user. Only for triggers that signed the
  // local user out: the `stale` flow keeps its user until submit, and is
  // resolved explicitly by the submit handlers instead.
  //
  // A prompt resumed from a redirect ceremony is NOT that case: the user
  // arriving is the ceremony's result, picked in a chooser, and is checked
  // like a popup's before anything stands down (AGL-3425).
  const verifyingRedirect = useRef(false)
  // The account a refused ceremony signed in, until its sign-out reaches this
  // render: the effect below must not read it as a session that came back.
  const refusedUid = useRef<string | null>(null)
  useEffect(() => {
    if (!active || !state.resumedFromRedirect || !user || busy) return
    if (verifyingRedirect.current) return
    if ((user as { uid?: string }).uid === refusedUid.current) return
    verifyingRedirect.current = true
    void (async () => {
      // The redirect's credential is the only thing that can say the
      // account was created just now; a read that fails or was already
      // consumed leaves the uid comparison to decide.
      const credential = await getRedirectResult(auth).catch(() => null)
      const refusal = reauthRefusal(state.identity, credential, user as any)
      if (refusal) {
        refusedUid.current = (user as { uid?: string }).uid ?? null
        await standDownRefusedReauth(auth, refusal, (credential?.user ?? user) as any)
        setError(reauthRefusalText(refusal, state.identity.email))
      } else {
        clearSessionReauth()
        clearSignInBounces()
      }
      verifyingRedirect.current = false
    })()
  }, [active, state.resumedFromRedirect, state.identity, user, busy, auth])

  useEffect(() => {
    if (
      active &&
      state.requiresSignIn &&
      !state.resumedFromRedirect &&
      user &&
      !busy &&
      (user as { uid?: string }).uid !== refusedUid.current
    ) {
      clearSessionReauth()
      // The round trip settled, so the loop breaker's evidence is spent
      // (AGL-2486). Cleared HERE and at the sign-in success below — never
      // in the layout's "signed in" branch, which a flapping session
      // reaches on every cycle and would reset the counter forever.
      clearSignInBounces()
    }
  }, [active, state.requiresSignIn, state.resumedFromRedirect, user, busy])

  // A fresh request must not inherit the previous attempt's leftovers.
  useEffect(() => {
    if (!active) {
      setPassword('')
      setError(null)
    }
  }, [active])

  /**
   * The one flow, for every factor: end whatever half-session is left,
   * then perform a REAL sign-in. The interactive markers route the
   * sign-out and the fresh mint through `useSessionCookie`'s convergence
   * branches (AGL-543/AGL-463), same as the /signout page and the sign-in
   * page do — this dialog invents no session machinery of its own.
   */
  const signInAgain = useCallback(
    async (credentialSignIn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        if (user) {
          // Still locally signed in (the `stale` trigger): the heal is a
          // full sign-out first — an in-place token refresh provably does
          // not clear a stale session's denied reads (AGL-1062).
          markInteractiveSignOut()
          await signOut(auth)
        }
        // BEFORE the sign-in starts, so it survives a mobile redirect
        // round-trip and the session hook mints rather than validates a
        // stale cookie on return (AGL-463).
        markInteractiveSignIn()
        await setPersistence(auth, browserLocalPersistence)
        const credential = await credentialSignIn()
        // The account that came back must be the one this prompt is for,
        // and must not be one the ceremony just created (AGL-3425).
        const refusal = reauthRefusal(state.identity, credential)
        if (refusal) {
          refusedUid.current =
            (credential as { user?: { uid?: string } } | undefined)?.user?.uid ??
            null
          await standDownRefusedReauth(
            auth,
            refusal,
            (credential as { user?: unknown } | undefined)?.user as any,
          )
          setError(reauthRefusalText(refusal, state.identity.email))
          return
        }
        clearSessionReauth()
        clearSignInBounces()
      } catch (caught) {
        const failure = caught as { code?: string; message?: string }
        // A classified passkey failure already carries copy written for a
        // person; the code table would flatten it back to a generic line.
        setError(
          failure?.code?.startsWith('auth/passkey-')
            ? [failure.message, AuthErrorMessage[failure.code]]
                .filter(Boolean)
                .join(' ')
            : signInErrorText(failure?.code),
        )
      } finally {
        setBusy(false)
      }
    },
    [auth, user, state.identity],
  )

  const handlePasswordSubmit = useCallback(() => {
    const email = state.identity.email
    if (!email || !password) return
    void signInAgain(() => signInWithEmailAndPassword(auth, email, password))
  }, [auth, password, signInAgain, state.identity.email])

  const handleProviderClick = useCallback(() => {
    const provider = providerForId(state.identity.providerId)
    void signInAgain(() => {
      // Mobile popups become tabs whose result never reaches the SDK
      // (AGL-462); the redirect flow returns to THIS route and completes
      // on load, with the interactive marker minting the shared cookie.
      if (!isMobileBrowser()) return signInWithPopup(auth, provider)
      /*
       * The redirect LEAVES the page, and `signInAgain` has already signed
       * this user out — so the tab returns unauthenticated with the prompt
       * that was holding it erased by the reload, while Firebase is still
       * resolving the redirect result. Without this breadcrumb the layout
       * reads "not signed in, nothing pending" and sends the reader to
       * /signin: all the way back, from the dialog that promised they would
       * stay where they are.
       */
      markSessionReauthRedirect()
      return signInWithRedirect(auth, provider)
    })
  }, [auth, signInAgain, state.identity.providerId])

  const handlePasskeyClick = useCallback(() => {
    void signInAgain(() =>
      signInWithPasskey(auth).catch((caught) => {
        // Classified, never relabelled "cancelled" (AGL-1417): WebAuthn also
        // returns NotAllowedError when no credential exists for the RP ID,
        // and this dialog's own comment used to assume that case was
        // harmless — it is the one that leaves the user stuck.
        throw describePasskeySignInFailure(caught)
      }),
    )
  }, [auth, signInAgain])

  const handleUseSignInPage = useCallback(() => {
    // Escape hatch (an account whose factors we could not capture, or a
    // user who simply prefers the full page): the classic flow, with the
    // current route carried in `continue` so it still resumes here.
    window.location.assign(
      `/signin?continue=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`,
    )
  }, [])

  if (!active) return null

  if (state.dismissed) {
    // "Not now" (only shown once the local user is signed out): the route
    // stays, cached content stays, and this is the way back in.
    //
    // The `stale` trigger gets NO banner here, and since AGL-2486 there is
    // no session-health banner behind it either — that was the whole point
    // of the change. Its user is still signed in and the page still works
    // off cache, so a dismissal leaves them exactly as they were; the way
    // back in is offered by the degraded lists themselves (`EmptyState`
    // reads this same store). Nothing re-opens this dialog on its own until
    // a read has reached the server again.
    if (!state.requiresSignIn) return null
    return (
      <Alert
        severity="warning"
        sx={{ borderRadius: 0, position: 'sticky', top: 0, zIndex: 1400 }}
        action={
          <Button color="inherit" size="small" onClick={reopenSessionReauth}>
            {'Sign in'}
          </Button>
        }
      >
        {'You are signed out — pages are read-only and may be out of date. ' +
          'Nothing has been lost; sign in again to keep working.'}
      </Alert>
    )
  }

  const email = state.identity.email
  const showPasswordForm = state.identity.hasPassword && Boolean(email)
  const providerLabel = state.identity.providerId?.startsWith('saml.')
    ? 'Continue with single sign-on'
    : state.identity.providerId?.startsWith('oidc.')
      ? 'Continue with single sign-on'
      : 'Continue with Google'
  const showProviderButton = Boolean(state.identity.providerId)

  return (
    <Dialog open maxWidth="xs" fullWidth onClose={busy ? undefined : dismissSessionReauth}>
      <DialogTitle>{'Sign in again to verify your device'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>{reasonText(state)}</DialogContentText>
          {email ? (
            <Typography variant="body2" color="text.secondary">
              {email}
            </Typography>
          ) : null}
          {showPasswordForm ? (
            <TextField
              type="password"
              label="Password"
              value={password}
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handlePasswordSubmit()
              }}
              fullWidth
            />
          ) : null}
          {showProviderButton ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={handleProviderClick}
            >
              {providerLabel}
            </Button>
          ) : null}
          {passkeySupported ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={handlePasskeyClick}
            >
              {'Use a passkey'}
            </Button>
          ) : null}
          {!showPasswordForm && !showProviderButton && !passkeySupported ? (
            <Alert severity="info">
              {'Use the sign-in page to continue — this page will resume ' +
                'after you sign in.'}
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2">
            <Button
              variant="text"
              size="small"
              disabled={busy}
              onClick={handleUseSignInPage}
            >
              {'Use the sign-in page instead'}
            </Button>
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={dismissSessionReauth} disabled={busy}>
          {'Not now'}
        </Button>
        {showPasswordForm ? (
          <Button
            variant="contained"
            disabled={busy || !password}
            onClick={handlePasswordSubmit}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
SessionReauthDialog.displayName = 'SessionReauthDialog'

export default SessionReauthDialog
